import type { IndustryDefinition } from '../types/content';

export const industries: IndustryDefinition[] = [
  {
    slug: 'healthcare-and-health-technology',
    name: 'Healthcare and Health Technology',
    overview:
      'Leadership appointments across provider systems, payers, physician groups and the technology businesses serving them. Appointments in this sector carry clinical, regulatory and community obligations alongside commercial ones, and the assessment has to reflect that.',
    marketConsiderations: [
      'Clinical credibility and administrative capability are frequently held by different people; the specification must decide which the seat requires',
      'Reimbursement and regulatory change alters the leadership requirement faster than organizational structure adapts',
      'Health-system boards often include clinicians, community representatives and finance specialists with materially different criteria',
      'Digital-health leaders moving into provider organizations underestimate the length of the decision cycle',
    ],
    leadershipFunctions: [
      'Chief Executive and Chief Operating Officer',
      'Chief Medical Officer and Chief Clinical Officer',
      'Chief Nursing Officer',
      'Chief Financial Officer and revenue-cycle leadership',
      'Chief Information and Digital Officer',
      'Population health and value-based care leadership',
      'Quality, safety and clinical governance',
    ],
    sampleRoleCategories: [
      'Health-system Chief Operating Officer',
      'Regional President, provider network',
      'Chief Medical Officer, payer organization',
      'Chief Commercial Officer, health technology',
      'Vice President, Value-Based Care',
    ],
    relatedArticleSlugs: ['clinical-and-commercial-leadership', 'assessing-leaders-for-scope-they-have-not-held'],
    publishState: 'published',
  },
  {
    slug: 'financial-services',
    name: 'Financial Services',
    overview:
      'Banking, insurance, asset and wealth management, and the infrastructure businesses beneath them. Regulatory accountability sits on named individuals here, which changes both the specification and the reference process.',
    marketConsiderations: [
      'Regulatory approval requirements can extend an appointment timeline well beyond offer acceptance',
      'Deferred compensation structures materially affect candidate mobility and must be understood before approach',
      'Control-function independence constrains who may credibly move between roles inside one institution',
      'Technology and data leadership in regulated institutions requires a different profile from the same title elsewhere',
    ],
    leadershipFunctions: [
      'Chief Executive and divisional President',
      'Chief Financial Officer and Treasurer',
      'Chief Risk Officer and Chief Compliance Officer',
      'Chief Investment Officer',
      'Chief Technology and Chief Data Officer',
      'Head of Commercial or Corporate Banking',
    ],
    sampleRoleCategories: [
      'Chief Risk Officer, regional bank',
      'Chief Investment Officer, insurance balance sheet',
      'Head of Wealth Management',
      'Chief Financial Officer, asset manager',
      'Managing Director, Corporate Banking',
    ],
    relatedArticleSlugs: ['compensation-benchmarks-and-their-limits'],
    publishState: 'published',
  },
  {
    slug: 'technology-data-and-artificial-intelligence',
    name: 'Technology, Data and Artificial Intelligence',
    overview:
      'Software, platform, infrastructure and data businesses, and the technology leadership of organizations outside the sector. Titles in this market are unusually unreliable indicators of scope, so research is calibrated to scope rather than title.',
    marketConsiderations: [
      'The same title spans an order of magnitude of scope; benchmarking must be done on scale, spend and organizational complexity',
      'Applied-AI leadership requirements are frequently written as research requirements, which narrows the population unnecessarily',
      'Equity value in the current position is often the decisive factor, not base compensation',
      'Leaders from hyperscale environments do not always transfer to organizations without the same platform maturity',
    ],
    leadershipFunctions: [
      'Chief Technology Officer and Chief Product Officer',
      'Chief Information Officer',
      'Chief Data and Analytics Officer',
      'Head of Artificial Intelligence or Machine Learning',
      'Chief Revenue Officer and Chief Customer Officer',
      'Vice President, Platform Engineering',
    ],
    sampleRoleCategories: [
      'Chief Technology Officer, enterprise software',
      'Chief Data and Analytics Officer',
      'Vice President, Applied Machine Learning',
      'Chief Product Officer, vertical SaaS',
      'Head of Platform Engineering',
    ],
    relatedArticleSlugs: ['what-ai-leadership-specifications-get-wrong', 'assessing-leaders-for-scope-they-have-not-held'],
    publishState: 'published',
  },
  {
    slug: 'cybersecurity',
    name: 'Cybersecurity',
    overview:
      'Security leadership inside enterprises and in the vendor market. The role has moved from technical stewardship to board-facing risk accountability, and many specifications have not caught up.',
    marketConsiderations: [
      'Board reporting expectations now form part of the requirement, not an adjacent nicety',
      'Personal liability exposure is a live consideration for candidates and affects negotiation',
      'Regulated-sector security leadership requires demonstrable regulatory engagement, not only technical depth',
      'Vendor-side and enterprise-side security careers have diverged; movement between them is less common than assumed',
    ],
    leadershipFunctions: [
      'Chief Information Security Officer',
      'Deputy or Divisional CISO',
      'Head of Security Architecture',
      'Head of Security Operations and Incident Response',
      'Head of Governance, Risk and Compliance',
      'Chief Security Officer, vendor organization',
    ],
    sampleRoleCategories: [
      'Chief Information Security Officer, regulated enterprise',
      'Vice President, Security Operations',
      'Head of Security Architecture',
      'Field Chief Information Security Officer',
    ],
    relatedArticleSlugs: ['the-board-facing-security-leader'],
    publishState: 'published',
  },
  {
    slug: 'aerospace-and-defense',
    name: 'Aerospace and Defense',
    overview:
      'Primes, tier-one suppliers, space businesses and defense-technology entrants. Clearance status, programme experience and government-customer fluency narrow the population considerably.',
    marketConsiderations: [
      'Security clearance status is a genuine constraint and must be established before approach, without collecting sensitive personal data',
      'Programme-execution credibility is assessed on specific programmes, not on general tenure',
      'Movement between traditional primes and defense-technology entrants requires cultural assessment as much as technical',
      'Government-customer relationships are institutional as well as personal; the specification should be explicit about which is required',
    ],
    leadershipFunctions: [
      'Business-unit President and General Manager',
      'Chief Engineer and Vice President, Engineering',
      'Vice President, Programmes',
      'Chief Operating Officer, manufacturing and supply',
      'Vice President, Business Development and Capture',
      'Chief Compliance Officer, government contracting',
    ],
    sampleRoleCategories: [
      'General Manager, defense business unit',
      'Vice President, Programme Execution',
      'Chief Engineer, space systems',
      'Vice President, Capture and Business Development',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'manufacturing-and-industrial',
    name: 'Manufacturing and Industrial',
    overview:
      'Discrete and process manufacturing, building products, automotive supply and diversified industrials. Appointments here are judged on plant-level and network-level operating results.',
    marketConsiderations: [
      'Operating leadership is credible only with evidence at the relevant asset scale and complexity',
      'Location constraints are more binding than in most sectors and should be tested early',
      'Continuous-improvement fluency is claimed universally and evidenced rarely',
      'Energy cost and reshoring decisions are reshaping the operating leadership requirement',
    ],
    leadershipFunctions: [
      'Chief Operating Officer and Vice President, Operations',
      'Plant and multi-site General Manager',
      'Vice President, Manufacturing Engineering',
      'Chief Supply Chain Officer',
      'Vice President, Quality and Continuous Improvement',
      'Chief Commercial Officer, industrial',
    ],
    sampleRoleCategories: [
      'Chief Operating Officer, diversified industrial',
      'Vice President, Multi-Site Operations',
      'General Manager, business unit',
      'Vice President, Manufacturing Engineering',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'private-equity-and-portfolio-operations',
    name: 'Private Equity and Portfolio Operations',
    overview:
      'Fund-level operating talent and portfolio-company leadership across the deal cycle. Stage experience matters more than sector experience in most of these appointments.',
    marketConsiderations: [
      'Experience of the relevant stage — carve-out, roll-up, turnaround, exit preparation — is the primary screen',
      'Incentive alignment must be discussed candidly and early or the process wastes everyone’s time',
      'Hold-period pressure compresses the process without reducing the evidence required',
      'Sponsor-backed operating experience does not transfer automatically between fund strategies',
    ],
    leadershipFunctions: [
      'Portfolio-company Chief Executive and Chief Financial Officer',
      'Operating Partner and Operating Director',
      'Value-creation and commercial-excellence leadership',
      'Integration and carve-out leadership',
      'Chair and Non-Executive Director, portfolio board',
    ],
    sampleRoleCategories: [
      'Portfolio-company Chief Executive',
      'Operating Partner, value creation',
      'Chief Financial Officer, carve-out',
      'Chair, portfolio board',
    ],
    relatedArticleSlugs: ['leadership-in-the-first-hundred-days'],
    publishState: 'published',
  },
  {
    slug: 'consumer-and-retail',
    name: 'Consumer and Retail',
    overview:
      'Brand owners, retailers, food and beverage, and direct-to-consumer businesses. Commercial leadership here is assessed on demonstrable brand and margin outcomes.',
    marketConsiderations: [
      'Channel economics differ enough that cross-channel moves need explicit assessment',
      'Brand-building and performance-marketing capability are distinct and frequently conflated in specifications',
      'Retail operating leadership is bound by geography more than most sectors',
      'Private-label and branded career paths diverge early and rarely reconverge',
    ],
    leadershipFunctions: [
      'Chief Executive and divisional President',
      'Chief Commercial and Chief Marketing Officer',
      'Chief Merchandising Officer',
      'Chief Supply Chain Officer',
      'Chief Digital and Omnichannel Officer',
      'Chief Financial Officer, consumer',
    ],
    sampleRoleCategories: [
      'Chief Marketing Officer, branded consumer',
      'Chief Merchandising Officer',
      'President, direct-to-consumer',
      'Vice President, Omnichannel Operations',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'professional-services',
    name: 'Professional Services',
    overview:
      'Consulting, legal, accounting and specialist advisory firms. Partnership economics and client-following make these appointments structurally different from corporate ones.',
    marketConsiderations: [
      'Client-portfolio portability is frequently overstated and must be tested carefully and lawfully',
      'Partnership admission processes sit outside the search timetable and must be accommodated',
      'Restrictive covenants materially shape what is possible and when',
      'Firm-building capability is distinct from personal delivery excellence',
    ],
    leadershipFunctions: [
      'Managing Partner and Chief Executive',
      'Practice and Sector Leader',
      'Chief Operating Officer, firm',
      'Chief Growth and Chief Client Officer',
      'Chief Talent Officer',
    ],
    sampleRoleCategories: [
      'Practice Leader, advisory',
      'Chief Operating Officer, professional-services firm',
      'Managing Partner, regional office',
      'Chief Growth Officer',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'energy-and-infrastructure',
    name: 'Energy and Infrastructure',
    overview:
      'Utilities, renewables, midstream, transmission and infrastructure funds. Capital-project delivery and regulatory relationships dominate the requirement.',
    marketConsiderations: [
      'Regulated-utility and merchant-market leadership are different disciplines with different risk postures',
      'Major capital-project delivery credibility is evidenced by named projects and outcomes',
      'Energy-transition roles are frequently specified as though the incumbent market already exists',
      'Location and asset proximity constrain candidate availability materially',
    ],
    leadershipFunctions: [
      'Chief Executive and Chief Operating Officer',
      'Chief Development Officer, renewables',
      'Vice President, Capital Projects and Construction',
      'Chief Regulatory and External Affairs Officer',
      'Chief Financial Officer, infrastructure',
      'Asset-management leadership',
    ],
    sampleRoleCategories: [
      'Chief Operating Officer, regulated utility',
      'Chief Development Officer, renewables platform',
      'Vice President, Transmission Capital Projects',
      'Head of Asset Management, infrastructure fund',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'life-sciences-and-pharmaceuticals',
    name: 'Life Sciences and Pharmaceuticals',
    overview:
      'Biotechnology, pharmaceutical, medical-device and diagnostics organizations. Development-stage experience is the dominant variable in these appointments.',
    marketConsiderations: [
      'Stage experience — discovery, clinical, registration, commercial launch — is the primary screen',
      'Regulatory-agency relationships and submission history are specific and verifiable',
      'Therapeutic-area transfer is more feasible than stage transfer, and specifications often assume the reverse',
      'Clinical-development leadership requires medical qualification in many but not all seats; the specification should say which',
    ],
    leadershipFunctions: [
      'Chief Executive and Chief Business Officer',
      'Chief Medical Officer and Chief Development Officer',
      'Chief Scientific Officer',
      'Chief Regulatory Affairs Officer',
      'Chief Commercial Officer and launch leadership',
      'Chief Technical Operations and Manufacturing Officer',
    ],
    sampleRoleCategories: [
      'Chief Medical Officer, clinical-stage biotechnology',
      'Chief Commercial Officer, launch organization',
      'Vice President, Regulatory Affairs',
      'Chief Technical Operations Officer',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'supply-chain-and-logistics',
    name: 'Supply Chain and Logistics',
    overview:
      'Third-party logistics, freight, warehousing, and the supply-chain function inside shipper organizations. Network design and cost-to-serve outcomes are the evidence base.',
    marketConsiderations: [
      'Asset-based and non-asset-based logistics leadership are distinct career tracks',
      'Network redesign credibility is evidenced by named transformations and measured outcomes',
      'Supply-chain leadership inside shippers is increasingly a commercial role as much as an operating one',
      'Automation programme experience is claimed widely and evidenced narrowly',
    ],
    leadershipFunctions: [
      'Chief Supply Chain Officer',
      'Chief Operating Officer, logistics',
      'Vice President, Network Design and Planning',
      'Vice President, Transportation and Fleet',
      'Vice President, Warehouse Operations and Automation',
      'Chief Procurement Officer',
    ],
    sampleRoleCategories: [
      'Chief Supply Chain Officer',
      'Vice President, Network Design',
      'Chief Operating Officer, third-party logistics',
      'Vice President, Warehouse Automation',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'human-resources-and-people-leadership',
    name: 'Human Resources and People Leadership',
    overview:
      'Chief human resources officers and the specialist leadership beneath them. These appointments are frequently made at the point of organizational change, which shapes the requirement.',
    marketConsiderations: [
      'Transformation, scale-up and steady-state people leadership call for different profiles and are often specified interchangeably',
      'Total-rewards and labour-relations depth are specialist and not interchangeable with generalist experience',
      'Chief human resources officer appointments increasingly require board-committee fluency',
      'People-analytics capability is now a core requirement rather than a differentiator',
    ],
    leadershipFunctions: [
      'Chief Human Resources Officer and Chief People Officer',
      'Chief Talent Officer',
      'Vice President, Total Rewards',
      'Vice President, Labour and Employee Relations',
      'Head of Organizational Effectiveness',
      'Head of People Analytics',
    ],
    sampleRoleCategories: [
      'Chief Human Resources Officer',
      'Vice President, Total Rewards',
      'Chief Talent Officer',
      'Vice President, Labour Relations',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
  {
    slug: 'government-contracting',
    name: 'Government Contracting',
    overview:
      'Services and solutions businesses serving federal, state and local government. Contract-vehicle fluency and compliance discipline define the leadership requirement.',
    marketConsiderations: [
      'Contract-vehicle and agency-specific experience is a genuine screen, not a preference',
      'Capture and proposal leadership is a distinct discipline from delivery leadership',
      'Compliance obligations create personal accountability that candidates weigh carefully',
      'Commercial-sector leaders frequently underestimate the procurement cycle and the margin structure',
    ],
    leadershipFunctions: [
      'Sector or Group President',
      'Vice President, Capture and Growth',
      'Vice President, Programme Delivery',
      'Chief Compliance and Contracts Officer',
      'Chief Financial Officer, government services',
    ],
    sampleRoleCategories: [
      'Sector President, federal services',
      'Vice President, Capture Management',
      'Vice President, Programme Delivery',
      'Chief Contracts and Compliance Officer',
    ],
    relatedArticleSlugs: [],
    publishState: 'published',
  },
];

export const getIndustry = (slug: string): IndustryDefinition | undefined =>
  industries.find((i) => i.slug === slug);

export const industryNames = industries.map((i) => i.name);
